import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import hljs from 'highlight.js/lib/core';
import xml from 'highlight.js/lib/languages/xml'; // covers html
import json from 'highlight.js/lib/languages/json';
import bash from 'highlight.js/lib/languages/bash';
import python from 'highlight.js/lib/languages/python';
import javascript from 'highlight.js/lib/languages/javascript';
import typescript from 'highlight.js/lib/languages/typescript';
import sql from 'highlight.js/lib/languages/sql';
import yaml from 'highlight.js/lib/languages/yaml';
import css from 'highlight.js/lib/languages/css';
import java from 'highlight.js/lib/languages/java';
import csharp from 'highlight.js/lib/languages/csharp';
import plaintext from 'highlight.js/lib/languages/plaintext';
import { CodeCopyService } from '../services/code-copy.service';

// Curated language registration (core build, not the full hljs bundle) —
// done once at module scope so it only runs a single time per app load.
hljs.registerLanguage('html', xml);
hljs.registerLanguage('xml', xml);
hljs.registerLanguage('json', json);
hljs.registerLanguage('bash', bash);
hljs.registerLanguage('sh', bash);
hljs.registerLanguage('python', python);
hljs.registerLanguage('py', python);
hljs.registerLanguage('javascript', javascript);
hljs.registerLanguage('js', javascript);
hljs.registerLanguage('typescript', typescript);
hljs.registerLanguage('ts', typescript);
hljs.registerLanguage('sql', sql);
hljs.registerLanguage('yaml', yaml);
hljs.registerLanguage('yml', yaml);
hljs.registerLanguage('css', css);
hljs.registerLanguage('java', java);
hljs.registerLanguage('csharp', csharp);
hljs.registerLanguage('cs', csharp);
hljs.registerLanguage('plaintext', plaintext);

// Belt-and-suspenders: force every anchor (including any raw HTML `<a>` that
// bypasses the marked renderer.link override below) to open in a new tab.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.hasAttribute('href')) {
    node.setAttribute('target', '_blank');
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

/** Escapes text destined for an HTML attribute/text position. */
function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

/** Recursively flattens a marked inline-token tree down to its plain text. */
function tokensToPlainText(tokens: import('marked').Token[]): string {
  return tokens
    .map((token) => {
      const withChildren = token as { tokens?: import('marked').Token[]; text?: string; raw?: string };
      if (withChildren.tokens?.length) return tokensToPlainText(withChildren.tokens);
      if (typeof withChildren.text === 'string') return withChildren.text;
      return withChildren.raw ?? '';
    })
    .join('');
}

function truncateLabel(label: string, max = 28): string {
  const trimmed = label.trim();
  return trimmed.length > max ? `${trimmed.slice(0, max - 1)}…` : trimmed;
}

/** Returns the bare hostname (no `www.`) for an absolute http(s) URL, or null otherwise. */
function getCitationHostname(href: string): string | null {
  if (!/^https?:\/\//i.test(href)) return null;
  try {
    return new URL(href).hostname.replace(/^www\./i, '');
  } catch {
    return null;
  }
}

function faviconUrl(hostname: string): string {
  return `https://www.google.com/s2/favicons?sz=32&domain=${encodeURIComponent(hostname)}`;
}

/**
 * Builds a `marked.Renderer` whose `code` override produces a code block
 * with a header (language label + copy button) and hljs-highlighted body.
 * Built as a factory (rather than a bare module-scope singleton) so it can
 * close over the injected `CodeCopyService` instance — the renderer needs
 * that singleton to register each block's raw source for the copy button.
 */
function createMarkdownRenderer(codeCopyService: CodeCopyService) {
  const renderer = new marked.Renderer();

  renderer.code = ({ text, lang }: { text: string; lang?: string }): string => {
    let highlighted: string;
    let displayLanguage: string;

    const requestedLang = lang?.split(/\s+/)[0]?.toLowerCase();
    if (requestedLang && hljs.getLanguage(requestedLang)) {
      highlighted = hljs.highlight(text, { language: requestedLang }).value;
      displayLanguage = requestedLang;
    } else {
      const result = hljs.highlightAuto(text);
      highlighted = result.value;
      displayLanguage = result.language ?? 'plaintext';
    }

    const safeLang = escapeHtml(displayLanguage);
    const copyId = codeCopyService.register(text);

    return `<div class="code-block">
  <div class="code-block__header">
    <span class="code-block__lang">${safeLang}</span>
    <button type="button" class="code-block__copy" data-copy-id="${copyId}" aria-label="Copy code">
      <span class="code-block__copy-label">Copy</span>
    </button>
  </div>
  <pre class="hljs"><code class="hljs language-${safeLang}">${highlighted}</code></pre>
</div>`;
  };

  // Cited/linked sources should open in a new tab rather than navigating
  // away from the chat; noopener/noreferrer guards against tabnabbing.
  // Absolute http(s) links are rendered as a source "pill" (favicon + short
  // label) with a hover card showing the full title and site, matching the
  // citation treatment users are used to from other assistants. Links we
  // can't attribute to a site (relative paths, mailto:, anchors) fall back
  // to a plain anchor.
  renderer.link = ({ href, title, tokens }: { href: string; title?: string | null; tokens: import('marked').Token[] }): string => {
    const safeHref = escapeHtml(href);
    const hostname = getCitationHostname(href);

    if (!hostname) {
      const titleAttr = title ? ` title="${escapeHtml(title)}"` : '';
      const label = renderer.parser.parseInline(tokens);
      return `<a href="${safeHref}"${titleAttr} target="_blank" rel="noopener noreferrer">${label}</a>`;
    }

    const plainText = tokensToPlainText(tokens).trim() || hostname;
    const pillLabel = escapeHtml(truncateLabel(plainText));
    const tooltipTitle = escapeHtml(title?.trim() || plainText);
    const siteName = escapeHtml(hostname);
    const icon = faviconUrl(hostname);

    return `<a href="${safeHref}" target="_blank" rel="noopener noreferrer" class="citation-pill">` +
      `<img class="citation-pill__icon" src="${icon}" alt="" loading="lazy" referrerpolicy="no-referrer" />` +
      `<span class="citation-pill__label">${pillLabel}</span>` +
      `<svg class="citation-pill__arrow" viewBox="0 0 24 24" width="10" height="10" aria-hidden="true" focusable="false">` +
        `<path d="M7 17L17 7M17 7H8M17 7V16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/>` +
      `</svg>` +
      `<span class="citation-pill__tooltip" role="tooltip">` +
        `<span class="citation-pill__tooltip-title">${tooltipTitle}</span>` +
        `<span class="citation-pill__tooltip-site">` +
          `<img src="${icon}" alt="" loading="lazy" referrerpolicy="no-referrer" />${siteName}` +
        `</span>` +
      `</span>` +
    `</a>`;
  };

  return renderer;
}

@Pipe({ name: 'markdown', standalone: true })
export class MarkdownPipe implements PipeTransform {
  // Built once per pipe instance (not per transform call) but needs the
  // injected CodeCopyService singleton, so it's constructed in the
  // constructor rather than at bare module scope.
  private readonly renderer: ReturnType<typeof createMarkdownRenderer>;

  constructor(private sanitizer: DomSanitizer, private codeCopyService: CodeCopyService) {
    this.renderer = createMarkdownRenderer(this.codeCopyService);
  }

  transform(value: string | null | undefined): SafeHtml {
    if (!value) return '';
    const rawHtml = marked.parse(value, { async: false, renderer: this.renderer }) as string;
    const cleanHtml = DOMPurify.sanitize(rawHtml, {
      ALLOWED_TAGS: ['p', 'br', 'strong', 'b', 'em', 'i', 'ul', 'ol', 'li',
                     'table', 'thead', 'tbody', 'tr', 'th', 'td', 'code',
                     'pre', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote',
                     'a', 'hr', 'span', 'div', 's', 'del', 'button',
                     'img', 'svg', 'path'],
      ALLOWED_ATTR: ['href', 'target', 'rel', 'class', 'type', 'aria-label', 'data-copy-id',
                     'src', 'alt', 'loading', 'referrerpolicy', 'role',
                     'viewBox', 'width', 'height', 'aria-hidden', 'focusable',
                     'stroke', 'stroke-width', 'fill', 'stroke-linecap', 'stroke-linejoin', 'd'],
      FORCE_BODY: true,
    });
    return this.sanitizer.bypassSecurityTrustHtml(cleanHtml);
  }
}
